/**
 * @module tab/tab
 */

import { Plugin } from 'ckeditor5/src/core';
import { DowncastWriter } from 'ckeditor5/src/engine';
import { toWidget, viewToModelPositionOutsideModelElement, Widget } from 'ckeditor5/src/widget';
import TabCommand from './tabcommand';
import TabObserver from './tabobserver';

const TAB = 'tab';

/**
 * This plugin handles the <kbd>Tab</kbd> key (\t) in the editor.
 *
 * @extends {Plugin}
 */
export default class TabEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'TabEditing';
	}

	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [ Widget ];
	}

	/**
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );
		const config = editor.config.get( TAB ) || {};
		/**
		 * @type {string}
		 */
		this._tabValue = config.tabValue || '\t';
	}

	init() {
		const editor = this.editor;

		this._defineSchema();
		this._defineConverters();

		editor.commands.add( TAB, new TabCommand( editor ) );
		editor.editing.view.addObserver( TabObserver );
		editor.editing.mapper.on(
			'viewToModelPosition',
			viewToModelPositionOutsideModelElement( editor.model, viewElement => viewElement.hasClass( TAB ) )
		);

		this.listenTo( editor.editing.view.document, TAB, ( evt, data ) => {
			editor.execute( TAB );
			data.preventDefault();
			editor.editing.view.scrollToTheSelection();
		} );
	}

	/**
	 * @private
	 */
	_defineSchema() {
		/**
		 * @type {Schema}
		 */
		const schema = this.editor.model.schema;
		schema.register( TAB, {
			allowWhere: '$text',
			allowAttributesOf: '$text',
			isInline: true,
			isObject: true
		} );
	}

	/**
	 * @private
	 */
	_defineConverters() {
		/**
		 * @type {Conversion}
		 */
		const conversion = this.editor.conversion;
		conversion.for( 'upcast' )
			.elementToElement( {
				view: {
					name: 'span',
					classes: TAB
				},
				model: TAB
			} );

		conversion.for( 'dataDowncast' )
			.elementToElement( {
				model: TAB,
				view: ( modelItem, { writer: viewWriter } ) => createTabViewElement( viewWriter, this._tabValue )
			} );
		conversion.for( 'editingDowncast' )
			.elementToElement( {
				model: TAB,
				view: ( modelItem, { writer: viewWriter } ) => toWidget( createTabViewElement( viewWriter, this._tabValue ), viewWriter )
			} );
	}
}

/**
 * Creates a view element representing the tab.
 *
 * 	<span contenteditable="false" class="tab" style="white-space: pre-wrap; mso-tab-count:1;">\t</span>
 *
 * @param {DowncastWriter} viewWriter
 * @param {string} tabValue
 * @returns
 */
export function createTabViewElement( viewWriter, tabValue ) {
	const figure = viewWriter.createContainerElement( 'span', {
		class: TAB,
		style: 'word-break:break-all;white-space:pre-wrap;mso-tab-count:1;width:1cm;display:inline-block;'
	}, {
		isAllowedInsideAttributeElement: true
	} );
	viewWriter.insert( viewWriter.createPositionAt( figure, 0 ), viewWriter.createText( tabValue ) );

	return figure;
}
